export const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'];
export const VIDEO_EXTENSIONS = ['mp4', 'mov', 'avi', 'webm', 'mkv'];
export const DOCUMENT_EXTENSIONS = ['pdf', 'doc', 'docx', 'txt', 'rtf'];
export const MODEL_EXTENSIONS = ['stl', 'obj', 'ply'];
export const CBCT_EXTENSIONS = ['dcm'];

export type FilePreviewType = 'image' | 'pdf' | 'video' | 'stl' | 'cbct';

export const getFileExtension = (filename: string): string =>
    filename.split('.').pop()?.toLowerCase() || '';

export const getFilePreviewType = (fileName: string): FilePreviewType | null => {
    const ext = getFileExtension(fileName);
    if (IMAGE_EXTENSIONS.includes(ext)) return 'image';
    if (ext === 'pdf') return 'pdf';
    if (VIDEO_EXTENSIONS.includes(ext)) return 'video';
    if (ext === 'stl') return 'stl';
    if (CBCT_EXTENSIONS.includes(ext)) return 'cbct';
    return null;
};

interface PreviewableAssetShape {
    name: string;
    source: 'monday' | 'gcs';
    patientFileId?: string;
    storageCategory?: string | null;
    metadata?: Record<string, unknown> | null;
}

export interface AssetPreviewOptions {
    /** Host can trigger CBCT processing (FilesApi.cbctProcessing present):
     *  unprocessed CBCT scans become previewable, opening the processing gate. */
    cbctProcessing?: boolean;
}

export const isMondayStl = (asset: PreviewableAssetShape): boolean =>
    getFilePreviewType(asset.name) === 'stl' && asset.source === 'monday';

export const hasCbctSources = (asset: PreviewableAssetShape): boolean => {
    const meta = asset.metadata;
    return !!meta && typeof meta.manifest === 'string' && typeof meta.binary === 'string';
};

/**
 * Asset-aware preview type. CBCT scans are usually zip archives of DICOM
 * slices, so the extension alone can't identify them — a file whose stored
 * category (PatientFileType) is CBCT and that carries processed viewer refs
 * (manifest/binary) is treated as a CBCT regardless of its filename.
 */
export const getAssetPreviewType = (
    asset: PreviewableAssetShape,
    options?: AssetPreviewOptions
): FilePreviewType | null => {
    if (asset.storageCategory === 'CBCT' && (hasCbctSources(asset) || options?.cbctProcessing))
        return 'cbct';
    return getFilePreviewType(asset.name);
};

export const isPreviewableAsset = (
    asset: PreviewableAssetShape,
    options?: AssetPreviewOptions
): boolean => {
    const type = getAssetPreviewType(asset, options);
    if (!type) return false;
    if (type === 'stl' && asset.source === 'monday') return false;
    if (type === 'cbct' && !hasCbctSources(asset)) {
        // Only processable files (GCS-backed CBCT with an id) open the gate.
        return (
            !!options?.cbctProcessing &&
            asset.storageCategory === 'CBCT' &&
            asset.source === 'gcs' &&
            !!asset.patientFileId
        );
    }
    return true;
};

/**
 * User-selectable coarse storage categories (`PatientFileType`) surfaced in the
 * "Edit file" modal. System types (PROFILE_PICTURE, CHAT_ATTACHMENT) are
 * intentionally excluded. `value` is the backend enum; `label` is human-readable.
 */
export const EDITABLE_FILE_TYPE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
    { value: 'PHOTO', label: 'Photo' },
    { value: 'RADIOGRAPH', label: 'Radiograph' },
    { value: 'STL', label: 'STL' },
    { value: 'CBCT', label: 'CBCT' },
    { value: 'ADDITIONAL', label: 'Additional' }
];

/**
 * Per-category subcategories surfaced in the "Edit file" modal. Stored in the
 * `PatientFile.metadata` JSON under `metaKey` — matching existing backend
 * conventions (`photoType` for photos, `documentType` elsewhere) so the values
 * stay queryable. Categories not listed here have no subcategory field.
 */
export interface SubcategoryConfig {
    /** Metadata key the selected value is written to. */
    metaKey: string;
    options: ReadonlyArray<{ value: string; label: string }>;
}

export const SUBCATEGORY_BY_FILE_TYPE: Readonly<Record<string, SubcategoryConfig>> = {
    PHOTO: {
        metaKey: 'photoType',
        options: [
            { value: 'extraoral-frontal', label: 'Extraoral — frontal' },
            { value: 'extraoral-frontal-smiling', label: 'Extraoral — frontal smiling' },
            { value: 'extraoral-profile', label: 'Extraoral — profile' },
            { value: 'intraoral-frontal', label: 'Intraoral — frontal' },
            { value: 'intraoral-right', label: 'Intraoral — right buccal' },
            { value: 'intraoral-left', label: 'Intraoral — left buccal' },
            { value: 'intraoral-upper-occlusal', label: 'Intraoral — upper occlusal' },
            { value: 'intraoral-lower-occlusal', label: 'Intraoral — lower occlusal' }
        ]
    },
    RADIOGRAPH: {
        metaKey: 'documentType',
        options: [
            { value: 'pano', label: 'Panoramic' },
            { value: 'ceph', label: 'Cephalometric (lateral)' },
            { value: 'pa-ceph', label: 'Cephalometric (PA)' },
            { value: 'periapical', label: 'Periapical' },
            { value: 'bitewing', label: 'Bitewing' }
        ]
    },
    STL: {
        metaKey: 'documentType',
        options: [
            { value: 'upper-arch', label: 'Upper arch' },
            { value: 'lower-arch', label: 'Lower arch' },
            { value: 'bite', label: 'Bite' }
        ]
    }
};

export const categorizeAsset = (fileName: string): string => {
    const ext = getFileExtension(fileName);
    if (IMAGE_EXTENSIONS.includes(ext)) return 'Images';
    if (VIDEO_EXTENSIONS.includes(ext)) return 'Videos';
    if (DOCUMENT_EXTENSIONS.includes(ext)) return 'Documents';
    if (MODEL_EXTENSIONS.includes(ext)) return '3D Models';
    if (CBCT_EXTENSIONS.includes(ext)) return 'CBCT';
    return 'Others';
};

/**
 * Asset-aware category for the files-list grouping. Same as categorizeAsset,
 * except any file whose stored category (PatientFileType) is CBCT groups under
 * CBCT regardless of extension, since real scans are zip archives.
 */
export const categorizeAssetWithType = (asset: {
    name: string;
    storageCategory?: string | null;
}): string => {
    if (asset.storageCategory === 'CBCT') return 'CBCT';
    return categorizeAsset(asset.name);
};
